import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { useSearchParams } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  FileText, FileArchive, Loader2, PackageOpen, ExternalLink, ChevronRight, CheckCircle2,
} from "lucide-react";
import { apiGet, openDocument } from "@/lib/api";
import { ConsumEmailButton } from "@/components/ConsumEmailButton";
import { ConsumMultipluBar } from "@/components/ConsumMultipluBar";
import { Checkbox } from "@/components/ui/checkbox";

export default function DocumentePage() {
  const [comenzi, setComenzi] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [documente, setDocumente] = useState([]);
  const [loadingDocs, setLoadingDocs] = useState(false);
  const [opening, setOpening] = useState(null);
  const [bifate, setBifate] = useState([]);

  const toggleBifa = (id) => setBifate((b) => (b.includes(id) ? b.filter((x) => x !== id) : [...b, id]));
  const onConsumSent = (ids, log) => {
    setComenzi((cs) => cs.map((c) => (ids.includes(c.id) ? { ...c, consum_email: log } : c)));
    setBifate([]);
  };
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await apiGet("/comenzi");
      setComenzi(d.items || []);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const selectComanda = async (c) => {
    if (selected?.id === c.id) {
      setSelected(null);
      setDocumente([]);
      return;
    }
    setSelected(c);
    setDocumente([]);
    setLoadingDocs(true);
    try {
      const d = await apiGet(`/comenzi/${c.id}/documente`);
      setDocumente(d.documente || []);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoadingDocs(false);
    }
  };

  const [sp] = useSearchParams();
  const focusId = sp.get("comanda");
  useEffect(() => {
    const c = comenzi.find((x) => x.id === focusId);
    if (!c) return;
    setSelected((prev) => {
      if (prev?.id !== c.id) {
        setLoadingDocs(true);
        apiGet(`/comenzi/${c.id}/documente`)
          .then((d) => setDocumente(d.documente || []))
          .catch((e) => toast.error(e.message))
          .finally(() => setLoadingDocs(false));
      }
      return c;
    });
    setTimeout(() => document.getElementById(`comanda-${focusId}`)?.scrollIntoView({ behavior: "smooth", block: "center" }), 100);
  }, [focusId, comenzi]);

  const open = async (tip) => {
    if (!selected) return;
    setOpening(tip);
    try {
      await openDocument(selected.id, tip);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setOpening(null);
    }
  };

  return (
    <div data-testid="documente-page">
      <div className="mb-6">
        <h1 className="text-2xl md:text-3xl font-bold text-slate-900 tracking-tight">Documente de fabrica</h1>
        <p className="text-sm text-slate-500 mt-1">
          Alege o comanda, apoi deschide documentul dorit. Se genereaza fara pret, din datele comenzii.
        </p>
      </div>

      <div>
        <h2 className="text-base font-semibold text-slate-700 mb-3">
          Comenzi {comenzi.length > 0 && <span className="text-slate-400">({comenzi.length})</span>}
        </h2>
        {loading ? (
          <div className="py-16 flex justify-center text-slate-400"><Loader2 className="h-6 w-6 animate-spin" /></div>
        ) : comenzi.length === 0 ? (
          <Card className="py-14 flex flex-col items-center text-slate-400" data-testid="documente-empty">
            <PackageOpen className="h-9 w-9 mb-3" />
            <p className="text-sm">Nicio comanda importata.</p>
          </Card>
        ) : (
          <div className="space-y-2" data-testid="documente-comenzi-list">
            <ConsumMultipluBar
              bifate={comenzi.filter((c) => bifate.includes(c.id))}
              onClear={() => setBifate([])}
              onSent={onConsumSent}
            />
            {comenzi.map((c) => {
              const isOpen = selected?.id === c.id;
              return (
                <div key={c.id} id={`comanda-${c.id}`}>
                  <Card
                    data-testid={`doc-comanda-${c.nr_comanda}`}
                    onClick={() => selectComanda(c)}
                    className={`p-3 cursor-pointer transition-all flex items-center gap-3 ${
                      isOpen
                        ? "border-amber-400 bg-amber-50 shadow-sm"
                        : c.com_erp
                        ? "border-emerald-300 bg-emerald-50/40 hover:border-emerald-400 hover:shadow-sm"
                        : "hover:border-amber-300 hover:shadow-sm"
                    }`}
                  >
                    <div onClick={(e) => e.stopPropagation()} className="shrink-0 flex items-center">
                      <Checkbox
                        checked={bifate.includes(c.id)}
                        disabled={!c.com_erp}
                        onCheckedChange={() => toggleBifa(c.id)}
                        title={c.com_erp ? "Bifeaza pentru trimitere la Corina" : "Fara Nr. com ERP"}
                        data-testid={`doc-bifa-consum-${c.nr_comanda}`}
                      />
                    </div>
                    <div className="h-9 w-9 rounded-lg bg-slate-900 flex items-center justify-center shrink-0">
                      <FileArchive className="h-4.5 w-4.5 text-amber-400" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="font-bold text-slate-900 truncate text-sm">{c.nr_comanda}</div>
                      <div className="text-xs text-slate-500 truncate">
                        {c.client_nume || "(fara client)"} · {c.nr_pozitii} pozitii
                      </div>
                      {c.com_erp && (
                        <span
                          className="inline-flex items-center gap-1 rounded-full bg-emerald-100 text-emerald-700 px-2 py-0.5 text-[11px] font-semibold tabular-nums mt-1"
                          data-testid={`doc-com-erp-badge-${c.nr_comanda}`}
                        >
                          <CheckCircle2 className="h-3 w-3" /> COM-ERP {c.com_erp}
                        </span>
                      )}
                    </div>
                    <ChevronRight className={`h-4 w-4 shrink-0 transition-transform ${isOpen ? "rotate-90 text-amber-500" : "text-slate-300"}`} />
                  </Card>

                  {isOpen && (
                    <Card className="mt-2 mb-1 p-4 border-amber-200 bg-white" data-testid="documente-panel">
                      {loadingDocs ? (
                        <div className="py-8 flex justify-center text-slate-400"><Loader2 className="h-6 w-6 animate-spin" /></div>
                      ) : (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="documente-buttons">
                          {documente.map((doc) => (
                            <Button
                              key={doc.tip}
                              variant="outline"
                              data-testid={`doc-btn-${doc.tip}`}
                              disabled={opening === doc.tip}
                              onClick={() => open(doc.tip)}
                              className="justify-between h-auto py-3 px-4 text-left border-slate-200 hover:border-amber-400 hover:bg-amber-50"
                            >
                              <span className="flex items-center gap-2 font-medium text-slate-800">
                                <FileText className="h-4 w-4 text-amber-600" />
                                {doc.label}
                              </span>
                              {opening === doc.tip
                                ? <Loader2 className="h-4 w-4 animate-spin text-amber-600" />
                                : <ExternalLink className="h-4 w-4 text-slate-400" />}
                            </Button>
                          ))}
                          <ConsumEmailButton key={`consum-${c.id}-${c.consum_email?.trimis_la || ""}`} comanda={c} />
                        </div>
                      )}
                    </Card>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
